import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';

import { useMembershipsRelayQuery } from '../../hooks/membership/query/V2/useMembershipsRelay';
import { mapMembershipToLegacy } from '../../hooks/membership/query/V2/membershipMappers';
import { archiveMembership } from '../../api/memberships';
import useMembershipImages from '../../hooks/membership/query/useMembershipImages';
import { changeTab, history } from '../../utils';
import CustomToast from '../../components/Redesing/custom-toast';
import PerksModal from './PerksModal';

import type { ISystem } from '../../ts/interface/system.interface';
import type { IImage } from '../../ts/interface/image.interface';
import type { IMembership } from '../../ts/interface/membership.interface';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import CardView from '../../components/Redesing/card-view';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, ButtonGroup } from '@mui/material';
import MembershipPerks from './assets/membership-perks';
import MembershipLocationAccess from './assets/membership-location-access';
import MembershipPricing from './assets/membership-pricing';
import UserMembership from './assets/user-membership';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import MembershipDocumentsList from './assets/membership-documents-list';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import MembershipCardDetails from './assets/general-card-details';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplaceMembershipModal from './assets/replace-membership-modal';
import ArchiveRidersWarning from './assets/archive-riders-warning';
import { canReplaceMembership } from './membership-permissions';

interface IMembershipDetailProps {
  currentSystem: ISystem;
  systemLoaded: boolean;
  movaticAccess?: number;
}

const MembershipDetail = ({
  currentSystem,
  systemLoaded,
  movaticAccess,
}: IMembershipDetailProps) => {
  const isMovatic = canReplaceMembership(movaticAccess);
  const { membershipId, currentTab: currentActiveTab } = useParams<{
    membershipId: string;
    currentTab: string;
  }>();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [membersRefreshKey, setMembersRefreshKey] = useState(0);
  const [showPerksModal, setShowPerksModal] = useState(false);
  const [selectedPerkData, setSelectedPerkData] = useState({
    perkID: '',
    stationID: '',
  });
  const [currentTabV, setCurrentTabV] = useState('general');

  useEffect(() => {
    if (currentActiveTab !== currentTabV) {
      setCurrentTabV(currentActiveTab);
    }
  }, [currentActiveTab, currentTabV]);

  const handleTabsChange = (event: SyntheticEvent<Element, Event>, value: string) => {
    changeTab(
      `${membershipId}/${value}`,
      `${membershipId}/${currentTabV}`,
      'memberships'
    );
  };

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    refetch: refetchMembershipData,
    isFetched: isMembershipFetched,
  } = useMembershipsRelayQuery(
    {
      membershipId: membershipId,
    },
    {
      enabled: systemLoaded && !!membershipId,
    }
  );

  // Map GraphQL membership to legacy format
  const membership = useMemo((): IMembership | undefined => {
    if (!membershipData || membershipData.length === 0) return undefined;
    return mapMembershipToLegacy(membershipData[0]);
  }, [membershipData]);
  const rawMembership = membershipData?.[0];
  const isCurrentMembership = rawMembership?.id === membershipId;

  // Plans this one replaced point at it (old -> new) and carry an archive
  // date, so B can say "Replaces A". A live plan pointing here is a legacy
  // new -> old pointer (B on A's page), not a plan this one replaced.
  const { data: replacedPlans } = useMembershipsRelayQuery(
    {
      replacementMembershipOldId: rawMembership?.oldId ?? undefined,
      hasArchiveDate: true,
    },
    { enabled: Boolean(rawMembership?.oldId), limit: 20 }
  );
  const replacesNames = useMemo(
    () => (replacedPlans ?? []).map((plan) => plan.name),
    [replacedPlans]
  );

  const { data: membershipImagesData, isLoading: isLoadingMembershipImages } =
    useMembershipImages({ enabled: systemLoaded });
  const membershipImages = useMemo(
    () => (membershipImagesData as IImage[] | undefined) ?? [],
    [membershipImagesData]
  );

  function handlePerksCloseModal() {
    setShowPerksModal(false);
    setSelectedPerkData({
      perkID: '',
      stationID: '',
    });
  }

  const isLoaded = useCallback(() => {
    return (
      !isLoadingMembership &&
      !isLoadingMembershipImages &&
      systemLoaded &&
      isMembershipFetched
    );
  }, [isLoadingMembership, isLoadingMembershipImages, systemLoaded, isMembershipFetched]);

  const button = () => {
    if (membership?.archived_on && new Date(membership.archived_on) <= new Date()) {
      return null;
    }
    return [
      {
        startIcon: <EditIcon />,
        label: 'Edit',
        onClick: () => history.push(`/memberships/edit/${membership?.id}`),
      },
      {
        startIcon: <DeleteIcon />,
        label: 'Archive',
        onClick: () => setShowArchiveModal(!showArchiveModal),
      },
      ...(isMovatic
        ? [
            {
              startIcon: <SwapHorizIcon />,
              label: 'Replace with another plan…',
              disabled: !isCurrentMembership,
              onClick: () => {
                if (isCurrentMembership) setShowReplaceModal(true);
              },
            },
          ]
        : []),
    ];
  };

  const currentLogo = useMemo(() => {
    return membershipImages?.find(
      (image: IImage) =>
        image.id === membership?.logo_image_id &&
        (image.type === 'MembershipLogo' || image.type === 'SystemLogo')
    );
  }, [membershipImages, membership]);

  const getMembershipIcon = useMemo(() => {
    const logo = currentLogo;
    const imageStyle = {
      height: '45px',
      maxWidth: '45px',
    };
    return logo?.image ? (
      <img
        alt=""
        src={`data:image/png;base64,${logo.image}`}
        style={{ ...imageStyle, borderRadius: '50%', objectFit: 'cover' }}
      />
    ) : (
      <CardMembershipIcon />
    );
  }, [currentLogo]);

  const handleArchive = async () => {
    if (!membership?.id) return;
    try {
      await archiveMembership(membership.id);
      history.push('/memberships');
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `There was an error archiving the membership: ${error}`,
        duration: 5000,
      });
    } finally {
      setShowArchiveModal(false);
    }
  };

  return (
    <PageContainer isLoading={!isLoaded()}>
      <PageContent
        backLink={'/memberships'}
        avatarColor={currentLogo?.image && 'none'}
        contentTitle={'Memberships'}
        itemName={membership?.name}
        itemChipValue={membership?.id}
        chipLabel={'Membership ID'}
        detailIcon={getMembershipIcon}
        currentTabV={currentTabV}
        itemTopButtons={<IconButtonMenu buttons={button() || []} />}
        handleTabsChange={handleTabsChange}
        tabsList={[
          { label: 'General', value: 'general' },
          { label: 'Members', value: 'members' },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'general' && (
              <Grid container spacing={2}>
                <Grid xs={12} lg={4}>
                  <Stack spacing={2}>
                    <CardView
                      isLoading={!isLoaded()}
                      content={
                        <MembershipCardDetails
                          membership={membership as IMembership}
                          membershipImages={membershipImages}
                          currentSystem={currentSystem}
                          replacesNames={replacesNames}
                        />
                      }
                      title={'Membership Details'}
                    />
                  </Stack>
                </Grid>
                <Grid xs={12} lg={8}>
                  <Stack spacing={2}>
                    <>
                      <MembershipPerks
                        perks={membership?.perks.filter((perk) => perk.system_id) ?? []}
                      />
                      <MembershipLocationAccess
                        onAddClick={() => setShowPerksModal(!showPerksModal)}
                        locations={
                          membership?.perks.filter(
                            (perk) => !perk.system_id && perk.archived_on === null
                          ) ?? []
                        }
                      />
                      <MembershipPricing prices={membership?.prices || []} />
                      {membership?.auth_type === 'admin' && (
                        <MembershipDocumentsList docs={membership?.data?.docs || []} />
                      )}
                    </>
                  </Stack>
                </Grid>
              </Grid>
            )}
            {currentTabV === 'members' && (
              <UserMembership
                key={`${membershipId}:${membersRefreshKey}`}
                authType={membership?.auth_type || ''}
                membershipId={membershipId}
                systemId={currentSystem.gid}
              />
            )}
          </>
        }
      />
      <MovaticCustomModal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(!showArchiveModal)}
        title="Archive Membership"
        content={
          <>
            Are you sure you would like to archive this membership? This cannot be undone.
            <ArchiveRidersWarning
              membershipId={membership?.id}
              enabled={isMovatic && showArchiveModal && isCurrentMembership}
              onReplaceInstead={() => {
                if (!isCurrentMembership) return;
                setShowArchiveModal(false);
                setShowReplaceModal(true);
              }}
            />
          </>
        }
        action={
          <ButtonGroup aria-label="outlined primary button group">
            <Button variant="contained" onClick={handleArchive}>
              Submit
            </Button>
            <Button
              color={'secondary'}
              variant="contained"
              onClick={() => setShowArchiveModal(!showArchiveModal)}
            >
              Cancel
            </Button>
          </ButtonGroup>
        }
        customStyles={undefined}
        useContentDividers={undefined}
      />

      {isMovatic && rawMembership && (
        <ReplaceMembershipModal
          open={showReplaceModal}
          onClose={() => setShowReplaceModal(false)}
          membership={rawMembership}
          routeMembershipId={membershipId}
          onReplaced={() => {
            refetchMembershipData();
            // Refetch from page one, discarding accumulated member rows and cursors.
            setMembersRefreshKey((key) => key + 1);
          }}
        />
      )}

      {showPerksModal && membership && (
        <PerksModal
          openModal={showPerksModal}
          systemID={currentSystem.gid}
          locationIDs={membership.perks.map((perk) => perk.station_id)}
          membership={membership}
          perkData={selectedPerkData}
          refetchMembershipData={refetchMembershipData}
          handleCloseModal={handlePerksCloseModal}
        />
      )}
    </PageContainer>
  );
};

export default connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean };
    admin: { admin: { admin: { movaticAccess: number } } };
  }) => ({
    currentSystem: state.system.current,
    systemLoaded: state.system.isLoaded,
    movaticAccess: state.admin.admin.admin.movaticAccess,
  }),
  () => ({})
)(MembershipDetail);
